import { useState, useEffect, useRef, useCallback } from 'react';
import useCountdown from './useCountdown';

const useScreenTimeout = ({
  totalSeconds,
  notificationDelaySeconds,
  onTimeout
}) => {
  const notificationTimeoutRef = useRef(null);
  const timeoutStartedAtRef = useRef(null);
  const [showBanner, setShowBanner] = useState(false);

  const {
    startCountdown,
    setCountdown,
    countdownFinished,
    timeFormatCountdown
  } = useCountdown(totalSeconds, true);

  const syncTimeoutState = useCallback(() => {
    if (!timeoutStartedAtRef.current) {
      return;
    }

    const elapsedMs = Date.now() - timeoutStartedAtRef.current;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    const remainingCountdown = Math.max(totalSeconds - elapsedSeconds, 0);
    const remainingNotificationMs = notificationDelaySeconds * 1000 - elapsedMs;

    if (remainingCountdown <= 0) {
      setCountdown(0);
    } else {
      startCountdown(remainingCountdown);
    }

    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    if (remainingNotificationMs <= 0) {
      setShowBanner(true);
      return;
    }

    notificationTimeoutRef.current = setTimeout(() => {
      setShowBanner(true);
    }, remainingNotificationMs);
  }, [notificationDelaySeconds, setCountdown, startCountdown, totalSeconds]);

  useEffect(() => {
    timeoutStartedAtRef.current = Date.now();
    startCountdown(totalSeconds);

    syncTimeoutState();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncTimeoutState();
      }
    };

    const handleWindowFocus = () => {
      syncTimeoutState();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);

      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, [
    notificationDelaySeconds,
    startCountdown,
    syncTimeoutState,
    totalSeconds
  ]);

  useEffect(() => {
    if (!countdownFinished || !showBanner) {
      return;
    }

    onTimeout();
  }, [countdownFinished, showBanner, onTimeout]);

  const clear = useCallback(() => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
    timeoutStartedAtRef.current = null;
    setShowBanner(false);
  }, []);

  return { showBanner, formattedCountdown: timeFormatCountdown, clear };
};

export default useScreenTimeout;
